import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDismissable } from '../../hooks';

/**
 * A select you can type into.
 *
 * A native `<select>` cannot be searched, and these lists run to dozens of fund
 * houses and hundreds of managers — long enough that scrolling is the slow way
 * to reach a name you already know. So: an input that filters as you type,
 * arrow keys to move, Enter to take the highlighted row, Escape or a press
 * outside to close.
 *
 * Options are plain strings, or `{ value, label }` when what is displayed is
 * not what is selected — a fund's name reads better than its symbol, but the
 * symbol is what the caller needs back. The input shows the current selection
 * when closed and the query while open, so typing over an existing choice
 * replaces it rather than editing it.
 *
 * Options may also be **grouped**: `{ label, options: [...] }` puts a heading
 * above a run of rows, the way the admin console's pickers do. That beats
 * prefixing every row ("Fund / model: X", "Fund / model: Y") — the category is
 * said once instead of on every line, and the names line up so they can be
 * scanned. A group whose rows are all filtered out takes its heading with it.
 */

/**
 * Rows mounted at once, across all groups.
 *
 * Every match is a DOM node, and the scheme master alone is ~1,400 options —
 * enough that opening the list janks before a single key is pressed. The cap is
 * on what is RENDERED, never on what is searched: the filter still runs over
 * every option, so a name is always reachable by typing it. High enough that
 * the ordinary pickers here — benchmarks, buckets, scenarios — never reach it.
 */
const MAX_ROWS = 60;

const asItem = (o) => (typeof o === 'string' ? { value: o, label: o } : o);

/** Both option shapes, flat and grouped, as one list of `{ label, items }`. */
function normalise(options) {
  const out = [];
  let ungrouped = null;
  (options ?? []).forEach((o) => {
    if (o && Array.isArray(o.options)) {
      out.push({ label: o.label ?? null, items: o.options.map(asItem) });
      ungrouped = null;
    } else if (o) {
      /* Loose options keep their order, collected under no heading — a caller
         that groups nothing behaves exactly as it did before. */
      if (!ungrouped) { ungrouped = { label: null, items: [] }; out.push(ungrouped); }
      ungrouped.items.push(asItem(o));
    }
  });
  return out;
}

export default function SearchSelect({
  id, label, value, options, onChange, placeholder = 'All', disabled, hint, emptyLabel = 'No matches',
  /* A picker whose value is the subject of the screen has nothing to clear to
     — there is no "no client". Those pass `clearable={false}` and get the
     dropdown caret in the corner instead of the clear cross. */
  clearable = true,
  /* Marks the label the way the form's other required fields are marked. */
  required = false,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const box = useRef(null);
  const list = useRef(null);
  /* Which input moved the highlight. Only the keyboard may scroll — see below. */
  const byKeyboard = useRef(false);

  const close = useCallback(() => {
    setOpen(false);
    setQuery('');
  }, []);
  useDismissable(open, box, close);

  const groups = useMemo(() => normalise(options), [options]);
  const items = useMemo(() => groups.flatMap((g) => g.items), [groups]);

  /* What the closed input shows: the chosen option's label, or the raw value
     when the selection is not in the list — a fund opened from elsewhere. */
  const selected = useMemo(
    () => (value == null ? '' : items.find((o) => o.value === value)?.label ?? value),
    [items, value],
  );

  /* Groups keep their heading only while something under it survives. */
  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return groups;
    return groups
      .map((g) => ({ ...g, items: g.items.filter((o) => o.label.toLowerCase().includes(q)) }))
      .filter((g) => g.items.length);
  }, [groups, query]);

  /* The cap, applied across groups in order and leaving a count of what it held
     back. A group the budget never reaches keeps its rows out of `shown`
     entirely rather than appearing as an empty heading. */
  const { shown, hidden } = useMemo(() => {
    let budget = MAX_ROWS;
    const out = [];
    let held = 0;
    matches.forEach((g) => {
      const take = budget > 0 ? g.items.slice(0, budget) : [];
      held += g.items.length - take.length;
      budget -= take.length;
      if (take.length) out.push({ ...g, items: take });
    });
    return { shown: out, hidden: held };
  }, [matches]);

  /* The keyboard walks the rows, not the headings, so navigation indexes this
     flattening of what is on screen rather than the rendered children — which
     is why it flattens `shown` and not `matches`: a row the cap held back is
     not reachable by arrow key either. */
  const flat = useMemo(() => shown.flatMap((g) => g.items), [shown]);
  const activeIndex = Math.min(active, Math.max(0, flat.length - 1));

  /* A filtered list should start at the top rather than wherever the previous
     one was left. */
  useEffect(() => { if (list.current) list.current.scrollTop = 0; }, [query]);

  /**
   * Keep the highlighted row on screen — for the keyboard only.
   *
   * `scrollIntoView` scrolls every scrollable ancestor, the page included, and
   * any scroll under a stationary cursor drags a different row beneath it,
   * firing `mouseenter`, which sets `active`, which scrolls again. That
   * feedback loop is what made the list run away on its own. So this scrolls
   * the `<ul>` and nothing else, and only when a key asked for it.
   */
  useEffect(() => {
    if (!open || !byKeyboard.current) return;
    byKeyboard.current = false;
    const ul = list.current;
    const li = ul?.querySelector(`[data-i="${activeIndex}"]`);
    if (!ul || !li) return;
    if (li.offsetTop < ul.scrollTop) ul.scrollTop = li.offsetTop;
    else if (li.offsetTop + li.offsetHeight > ul.scrollTop + ul.clientHeight) {
      ul.scrollTop = li.offsetTop + li.offsetHeight - ul.clientHeight;
    }
  }, [open, activeIndex]);

  const pick = (option) => {
    onChange(option.value);
    close();
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (!open) { setOpen(true); return; }
      byKeyboard.current = true;
      const step = e.key === 'ArrowDown' ? 1 : -1;
      setActive((i) => Math.min(flat.length - 1, Math.max(0, i + step)));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (open && flat[activeIndex]) pick(flat[activeIndex]);
      else setOpen(true);
    } else if (e.key === 'Escape' && open) {
      e.preventDefault();
      close();
    }
  };

  /* Rendered rows carry their position in `flat`, so a heading between two
     runs of options does not shift what the arrow keys are pointing at. */
  let cursor = -1;

  return (
    /* `ss-open` is what lifts the whole card above the content below it while
       the list is down — see the rule in theme/design.css. */
    <div className={`frow${open && !disabled ? ' ss-open' : ''}`} ref={box} style={{ minWidth: 240 }}>
      <label htmlFor={id}>
        {label}
        {required && <span style={{ color: 'var(--brand)' }}> *</span>}
      </label>

      {/* The list hangs off the INPUT, not off the field. Anchored to the field,
          `top: 100%` meant "below the label, the input, the hint and any height
          the field was stretched to by a taller neighbour in its row" — which
          opened the list a visible gap below the box it belongs to. */}
      <div style={{ position: 'relative' }}>
        {/* Says "type to search" before anyone has to discover that it can be typed into. */}
        <span
          aria-hidden="true"
          style={{
            position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)',
            color: 'var(--ink3)', fontSize: 14, lineHeight: 1, pointerEvents: 'none',
          }}
        >
          ⌕
        </span>
        <input
          id={id}
          className="fin"
          role="combobox"
          aria-expanded={open}
          aria-controls={`${id}-list`}
          autoComplete="off"
          disabled={disabled}
          placeholder={selected || placeholder}
          value={open ? query : selected}
          onChange={(e) => { setQuery(e.target.value); setActive(0); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          /* Room on the right for whichever corner mark is shown — the clear
             cross or the caret. One of the two is always there. */
          style={{ paddingLeft: 28, paddingRight: 30 }}
        />
        {value && !disabled && clearable ? (
          <button
            type="button"
            title={label ? `Clear ${label.toLowerCase()}` : 'Clear'}
            onClick={() => { onChange(null); close(); }}
            style={{
              position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
              border: 0, background: 'none', color: 'var(--ink3)', cursor: 'pointer',
              fontSize: 16, lineHeight: 1, padding: 4,
            }}
          >
            ×
          </button>
        ) : (
          /* Says the box opens a list, which an input alone does not. Inert:
             the input beneath it already opens on focus and on click. */
          <span
            aria-hidden="true"
            style={{
              position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
              color: 'var(--ink2)', fontSize: 17, lineHeight: 1, pointerEvents: 'none',
            }}
          >
            ▾
          </span>
        )}

        {open && !disabled && (
          <ul
            id={`${id}-list`}
            ref={list}
            role="listbox"
            style={{
              position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 40, marginTop: 4,
              maxHeight: 280, overflowY: 'auto', listStyle: 'none', padding: 4, margin: '4px 0 0',
              background: 'var(--panel)', border: '1px solid var(--line)', borderRadius: 10,
              boxShadow: '0 12px 32px rgba(0,0,0,.18)',
            }}
          >
            {flat.length ? (
              shown.map((g) => (
                <li key={g.label ?? ' ungrouped'} role="presentation">
                  {g.label && (
                    <div style={{
                      padding: '7px 10px 4px', fontSize: 10.2, fontWeight: 700, color: 'var(--ink3)',
                      textTransform: 'uppercase', letterSpacing: '.07em',
                    }}>
                      {g.label}
                    </div>
                  )}
                  <ul role="group" aria-label={g.label || undefined} style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                    {g.items.map((o) => {
                      cursor += 1;
                      const i = cursor;
                      return (
                        <li
                          key={o.value}
                          data-i={i}
                          role="option"
                          aria-selected={o.value === value}
                          onMouseEnter={() => setActive(i)}
                          onClick={() => pick(o)}
                          style={{
                            padding: '8px 10px', borderRadius: 7, cursor: 'pointer', fontSize: 13,
                            background: i === activeIndex ? 'color-mix(in srgb,var(--brand) 12%,transparent)' : 'transparent',
                            fontWeight: o.value === value ? 700 : 400,
                          }}
                        >
                          {o.label}
                        </li>
                      );
                    })}
                  </ul>
                </li>
              ))
            ) : (
              <li style={{ padding: '8px 10px', fontSize: 12.5, color: 'var(--ink3)' }}>{emptyLabel}</li>
            )}
            {/* Said outright, because a list that silently stops is one a reader
                assumes they have seen the end of. */}
            {hidden > 0 && (
              <li style={{ padding: '8px 10px', fontSize: 11.5, color: 'var(--ink3)', borderTop: '1px solid var(--line2)' }}>
                {hidden} more — type to narrow
              </li>
            )}
          </ul>
        )}
      </div>

      {hint && <span style={{ fontSize: 10.8, color: 'var(--ink3)' }}>{hint}</span>}
    </div>
  );
}
